import { useEffect, useState } from "react";
import api, { API } from "../lib/api";
import { getToken } from "../lib/tokenStorage";
import { useAuth } from "../lib/auth";
import { LogOut, User, FileText, Plus, MapPin, ClipboardList, BookOpen, ExternalLink, Image as ImgIcon, Plane, MessageCircle, Star, Wallet, Sparkles } from "lucide-react";
import ChatWidget from "../components/ChatWidget";
import PartnerHomeTile from "../components/PartnerHomeTile";
import PartnerMonthlySummary from "../components/PartnerMonthlySummary";
import ApplicationForm from "./partner/ApplicationForm";
import MyApplications from "./partner/MyApplications";
import WeekendTours from "./partner/WeekendTours";
import PrearrivalTab from "./partner/PrearrivalTab";
import ContentTab from "./partner/ContentTab";
import LogoUploader from "./partner/LogoUploader";
import ArrivalInfoTab from "./partner/ArrivalInfoTab";
import PartnerChat from "./partner/PartnerChat";
import FeedbackTab from "./partner/FeedbackTab";
import PaymentsTab from "./partner/PaymentsTab";
import ActivitiesTab from "./partner/ActivitiesTab";
import PartnerAmountDue from "../components/PartnerAmountDue";
import BrandColorPicker from "../components/BrandColorPicker";

const TABS = [
  { key: "dashboard", label: "Home", icon: User },
  { key: "apply", label: "New Application", icon: FileText },
  { key: "my-applications", label: "My Applications", icon: ClipboardList },
  { key: "arrivals", label: "Arrival Info", icon: Plane },
  { key: "weekend", label: "Pre-Booked Items", icon: MapPin },
  { key: "prearrival", label: "Pre-arrival", icon: BookOpen },
  { key: "activities", label: "Activities", icon: Sparkles },
  { key: "payments", label: "Payments", icon: Wallet },
  { key: "content", label: "Social Content", icon: ImgIcon },
  { key: "feedback", label: "Feedback", icon: Star },
  { key: "chat", label: "Chat with team", icon: MessageCircle },
];

export default function PartnerPortal() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [me, setMe] = useState(null);
  const [config, setConfig] = useState({});
  const [apps, setApps] = useState([]);
  const [weekend, setWeekend] = useState([]);
  const [staffPrograms, setStaffPrograms] = useState([]);
  const [staffWeekends, setStaffWeekends] = useState([]);

  const reload = async () => {
    const [mR, cR, aR, wR, sR] = await Promise.all([
      api.get("/partner-portal/me").catch(() => ({ data: null })),
      api.get("/partner-applications/config").catch(() => ({ data: {} })),
      api.get("/partner-applications").catch(() => ({ data: [] })),
      api.get("/partner-weekend-bookings").catch(() => ({ data: [] })),
      api.get("/partner-portal/staff-bookings").catch(() => ({ data: { program_bookings: [], weekend_bookings: [] } })),
    ]);
    setMe(mR.data); setConfig(cR.data); setApps(aR.data); setWeekend(wR.data);
    setStaffPrograms(sR.data?.program_bookings || []);
    setStaffWeekends(sR.data?.weekend_bookings || []);
  };
  useEffect(() => { reload(); }, []);

  // Compute a small palette derived from the partner's chosen accent colour.
  // We darken by ~12% for hover states and blend 92% white for the tint. If
  // no colour was chosen we keep the CSS defaults from index.css intact.
  const themeStyle = (() => {
    const hex = me?.partner_profile?.brand_color;
    if (!hex || !/^#[0-9a-f]{6}$/i.test(hex)) return undefined;
    const num = parseInt(hex.slice(1), 16);
    const r = (num >> 16) & 255, g = (num >> 8) & 255, b = num & 255;
    const darken = (v) => Math.max(0, Math.round(v * 0.82));
    const tint = (v) => Math.round(v + (255 - v) * 0.92);
    const toHex = (r, g, b) => "#" + [r, g, b].map(x => x.toString(16).padStart(2, "0")).join("");
    return {
      "--aii-green": hex,
      "--aii-green-dark": toHex(darken(r), darken(g), darken(b)),
      "--aii-green-tint": toHex(tint(r), tint(g), tint(b)),
    };
  })();

  return (
    <div className="min-h-screen bg-[color:var(--aii-bg)]" style={themeStyle}>
      <header className="bg-white border-b border-[color:var(--aii-border)] sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            {/* Whitelabel: partner's own logo + name lead the header. If the
                partner hasn't uploaded a logo yet we render their initial in
                a brand-tinted tile — but never the Aii Group mark. */}
            {(() => {
              const logoId = me?.profile_pic_id || me?.partner_profile?.logo_id;
              const orgName = me?.partner_profile?.org_name || me?.name || user.name;
              return logoId ? (
                <img src={`${API}/files/${logoId}?auth=${getToken()}`}
                     alt={orgName}
                     className="w-9 h-9 object-contain rounded"/>
              ) : (
                <div className="w-9 h-9 rounded bg-[color:var(--aii-green-tint)] flex items-center justify-center font-display font-bold text-brand" aria-hidden="true">
                  {(orgName || "?")[0].toUpperCase()}
                </div>
              );
            })()}
            <div className="min-w-0">
              <div className="font-display text-sm font-bold truncate" data-testid="partner-header-org">
                {me?.partner_profile?.org_name || me?.name || user.name}
              </div>
              <div className="text-[10px] text-[color:var(--aii-muted)]">Hi {user.name.split(" ")[0]}</div>
            </div>
          </div>
          <button onClick={logout} data-testid="logout-btn" className="text-xs px-3 py-1.5 border border-[color:var(--aii-border)] rounded hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1"><LogOut size={12}/> Sign out</button>
        </div>
        <nav className="max-w-5xl mx-auto px-2 pb-2 overflow-x-auto flex gap-1">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`ptab-${key}`}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 whitespace-nowrap ${tab === key ? "bg-brand text-white" : "text-[color:var(--aii-text-2)] hover:bg-[color:var(--aii-green-tint)]"}`}>
              <Icon size={12}/> {label}
            </button>
          ))}
        </nav>
      </header>
      <main className="max-w-5xl mx-auto p-4">
        {tab === "dashboard" && (
          <div className="space-y-4">
            <div className="aii-card p-5">
              <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Welcome</div>
              <h1 className="text-2xl font-display font-semibold">{me?.name || user.name}</h1>
              {me?.partner_profile && (
                <div className="mt-2 text-sm text-[color:var(--aii-text-2)] space-y-0.5">
                  <div>📍 {me.partner_profile.city}, {me.partner_profile.country}</div>
                  {me.partner_profile.address && <div>{me.partner_profile.address}</div>}
                  {me.partner_profile.website && <a href={me.partner_profile.website} target="_blank" rel="noopener noreferrer" className="text-brand hover:underline flex items-center gap-1"><ExternalLink size={11}/> {me.partner_profile.website}</a>}
                </div>
              )}
              <div className="mt-4 pt-4 border-t border-[color:var(--aii-border)]">
                <LogoUploader me={me} onUpdated={reload}/>
              </div>
              <BrandColorPicker me={me} onUpdated={reload}/>
            </div>
            <PartnerMonthlySummary />
            <PartnerHomeTile onNavigate={setTab} />
            <PartnerAmountDue onNavigate={setTab} />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button onClick={() => setTab("apply")} data-testid="cta-apply" className="aii-card p-4 text-left hover:border-brand hover:shadow-md transition">
                <Plus size={20} className="text-brand"/><div className="mt-2 font-display font-semibold">Submit new participant</div><div className="text-xs text-[color:var(--aii-muted)] mt-1">Application flows to Founder for approval.</div>
              </button>
              <button onClick={() => setTab("weekend")} data-testid="cta-weekend" className="aii-card p-4 text-left hover:border-brand hover:shadow-md transition">
                <MapPin size={20} className="text-brand"/><div className="mt-2 font-display font-semibold">Book a weekend tour</div><div className="text-xs text-[color:var(--aii-muted)] mt-1">Founder + BDOM approve, then it lands with the staff.</div>
              </button>
              <button onClick={() => setTab("prearrival")} data-testid="cta-prearrival" className="aii-card p-4 text-left hover:border-brand hover:shadow-md transition">
                <BookOpen size={20} className="text-brand"/><div className="mt-2 font-display font-semibold">Pre-arrival materials</div><div className="text-xs text-[color:var(--aii-muted)] mt-1">Videos + guides curated by your BDOM.</div>
              </button>
            </div>
          </div>
        )}
        {tab === "apply" && <ApplicationForm config={config} onSubmitted={() => { reload(); setTab("my-applications"); }} onBulkUploaded={() => { reload(); setTab("my-applications"); }}/>}
        {tab === "my-applications" && <MyApplications apps={apps} staffPrograms={staffPrograms} onDeleted={reload} onGotoTab={setTab}/>}
        {tab === "arrivals" && <ArrivalInfoTab apps={apps} staffPrograms={staffPrograms} staffWeekends={staffWeekends} onSubmitted={reload}/>}
        {tab === "weekend" && <WeekendTours rows={weekend} staffWeekends={staffWeekends} apps={apps} onSubmitted={reload}/>}
        {tab === "prearrival" && <PrearrivalTab/>}
        {tab === "activities" && <ActivitiesTab/>}
        {tab === "payments" && <PaymentsTab/>}
        {tab === "content" && <ContentTab/>}
        {tab === "feedback" && <FeedbackTab/>}
        {tab === "chat" && <PartnerChat/>}
      </main>
      <ChatWidget />
    </div>
  );
}
